import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@material-ui/core';
import { Form as FormikForm, Formik } from 'formik';

import {
  ClientDisclosureFragment,
  ClientDisclosureStatus,
  UpdateClientDisclosureInput,
  useUpdateClientDisclosureMutation,
} from 'api';
import { ValidatedSwitch, ValidatedTextField } from 'components';
import { useClient, useNotify } from 'hooks';
import { updateClientDisclosureResponse } from 'apollo/responses';
import { DisclosuresSchema } from 'features/admin/validations';
import { updateDisclosure as updateDisclosureReducer } from 'apollo/reducers';

interface IProps {
  disclosure: ClientDisclosureFragment;
}

const Update: FC<IProps> = ({ disclosure }) => {
  const { t } = useTranslation();
  const notify = useNotify();
  const client = useClient();

  const [updateClientDisclosureMutation] = useUpdateClientDisclosureMutation({
    onError: notify.mutationError,
    onCompleted: () => notify.info({ message: t('admin:disclosureUpdateNotification') }),
  });

  function checkStatus(value: ClientDisclosureStatus) {
    return value === ClientDisclosureStatus.Active;
  }

  function getNextStatus(value: ClientDisclosureStatus) {
    return value === ClientDisclosureStatus.Active
      ? ClientDisclosureStatus.Disabled
      : ClientDisclosureStatus.Active;
  }

  function handleSubmit(values: UpdateClientDisclosureInput) {
    return updateClientDisclosureMutation({
      variables: { data: values },
      optimisticResponse: updateClientDisclosureResponse(values),
      update: (cache, result) => {
        const { updateClientDisclosure } = result.data;
        updateDisclosureReducer({ cache, result: updateClientDisclosure, client_id: client.id });
      },
    });
  }

  return (
    <Formik initialValues={disclosure} onSubmit={handleSubmit} validationSchema={DisclosuresSchema}>
      {({ submitForm, setFieldValue, values }) => (
        <FormikForm>
          <Grid container>
            <Grid item xs={3} />
            <Grid item xs={7}>
              <Grid container item alignItems="center">
                <Grid item xs={11}>
                  <ValidatedTextField
                    name="disclosure"
                    i18nKey="admin:addDisclosure"
                    color="primary"
                    TextFieldProps={{ margin: 'none', label: null }}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={2} container alignItems="center" justify="flex-end">
              <ValidatedSwitch
                name="disclosure_status"
                checkedFn={checkStatus}
                onChange={() => {
                  setFieldValue('disclosure_status', getNextStatus(values.disclosure_status));
                  submitForm();
                }}
                color="primary"
              />
            </Grid>
          </Grid>
        </FormikForm>
      )}
    </Formik>
  );
};

export default Update;
